import { WalletAdapterNetwork } from '@solana/wallet-adapter-base';
import { ConnectionProvider, WalletProvider } from '@solana/wallet-adapter-react';
import { 
    PhantomWalletAdapter,
    SolflareWalletAdapter,
    TorusWalletAdapter,
    LedgerWalletAdapter,
    CoinbaseWalletAdapter,
    TrustWalletAdapter,
    SolongWalletAdapter,
    Coin98WalletAdapter
} from '@solana/wallet-adapter-wallets';
import { clusterApiUrl } from '@solana/web3.js';
import React, { useMemo, useCallback, useState, useEffect } from 'react';
import { WalletModalProvider } from '@solana/wallet-adapter-react-ui';
import { useToast } from '@chakra-ui/react';

export const WalletContextProvider = ({ children }) => {
    const toast = useToast();
    const [walletNotDetected, setWalletNotDetected] = useState(false);

    // The network can be set to 'devnet', 'testnet', or 'mainnet-beta'.
    const network = process.env.REACT_APP_SOLANA_NETWORK === 'mainnet' 
        ? WalletAdapterNetwork.Mainnet 
        : WalletAdapterNetwork.Devnet;

    // You can also provide a custom RPC endpoint.
    const endpoint = useMemo(() => {
        if (network === WalletAdapterNetwork.Devnet) {
            return process.env.REACT_APP_SOLANA_RPC_HOST || 'https://api.devnet.solana.com';
        } else if (network === WalletAdapterNetwork.Mainnet) {
            return process.env.REACT_APP_SOLANA_RPC_HOST || 'https://api.mainnet-beta.solana.com';
        }
        return clusterApiUrl(network);
    }, [network]);

    // Initialize wallet adapters for multiple wallet types
    const wallets = useMemo(
        () => [
            new PhantomWalletAdapter(),
            new SolflareWalletAdapter({ network }),
            new TorusWalletAdapter(),
            new LedgerWalletAdapter(),
            new CoinbaseWalletAdapter(),
            new TrustWalletAdapter(),
            new SolongWalletAdapter(),
            new Coin98WalletAdapter()
        ],
        [network]
    );

    // Check if any wallet extension is installed
    useEffect(() => {
        const checkWalletExtension = async () => {
            try {
                // Check if Phantom is installed
                const hasPhantom = window?.solana?.isPhantom;
                // Check if Solflare is installed
                const hasSolflare = window?.solflare?.isSolflare;
                
                if (!hasPhantom && !hasSolflare) {
                    setWalletNotDetected(true);
                    console.warn('No Solana wallet extension detected');
                    
                    // Only show toast if we're not in a password bypass situation
                    if (localStorage.getItem('palmheist_password_bypass') !== 'true') {
                        toast({
                            title: "No Wallet Detected",
                            description: "Please install a Solana wallet extension like Phantom or Solflare, or use password authentication.",
                            status: "warning",
                            duration: 5000,
                            isClosable: true,
                        });
                    }
                }
            } catch (error) {
                console.error('Error checking wallet extensions:', error);
            }
        };
        
        checkWalletExtension();
    }, [toast]);

    // Custom onError handler for wallet adapter
    const onError = useCallback(
        (error) => {
            console.error('Wallet error:', error);
            toast({
                title: "Wallet Connection Error",
                description: error.message || "Failed to connect wallet",
                status: "error",
                duration: 5000,
                isClosable: true,
            });
        },
        [toast]
    );

    return (
        <ConnectionProvider endpoint={endpoint}>
            <WalletProvider 
                wallets={wallets} 
                autoConnect={true} // Enable auto-connect to allow wallet reconnection
                onError={onError}
            >
                <WalletModalProvider>
                    {children}
                </WalletModalProvider>
            </WalletProvider>
        </ConnectionProvider>
    );
};

export default WalletContextProvider;
